import React, { useState } from 'react';
import BasePOIModal from './BasePOIModal';
import { MeasurementSelector } from './components/MeasurementSelector';
import { NotesAndPhotos } from './components/NotesAndPhotos';
import { WidthClearance } from './components/WidthClearance';

const CROSSING_TYPES = [
  { value: 'at_grade', label: 'At-Grade Crossing' },
  { value: 'grade_separated', label: 'Grade Separated' },
  { value: 'private', label: 'Private Crossing' }
];

const SURFACE_TYPES = [
  { value: 'rubber_panel', label: 'Rubber Panel' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'asphalt', label: 'Asphalt' },
  { value: 'timber', label: 'Timber' },
  { value: 'gravel', label: 'Gravel' }
];

const WARNING_DEVICES = [
  { value: 'gates', label: 'Gates' },
  { value: 'flashing_lights', label: 'Flashing Light' },
  { value: 'bells', label: 'Bells' },
  { value: 'advanced_warning', label: 'Advanced Warning Signs' }
];

export const RailroadPOIModal = ({ type, poiInfo, onClose, onSave, onBack }) => {
  const [formData, setFormData] = useState({
    crossingType: '',
    surfaceType: '',
    warningDevices: [],
    width: { feet: '', inches: '', meters: '' },
    crossingNumber: '',
    trackCount: '',
    railroadCompany: '',
    emergencyPhone: '',
    touchLevel: null,
    measurementType: 'touch',
    customHeight: { feet: '', inches: '', meters: '' },
    notes: '',
    photos: []
  });

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.crossingType) errors.crossingType = 'Crossing type is required';
    if (!formData.crossingNumber) errors.crossingNumber = 'Crossing number is required';
    if (formData.measurementType === 'touch' && !formData.touchLevel) {
      errors.measurements = 'Touch level is required';
    }
    if (formData.measurementType === 'custom' && 
        !formData.customHeight.feet && 
        !formData.customHeight.meters) {
      errors.measurements = 'Height measurement is required';
    }

    if (Object.keys(errors).length > 0) {
      return { success: false, errors };
    }

    // Prepare measurements array
    const measurements = [];
    if (formData.measurementType === 'touch') {
      measurements.push({
        type: 'touch',
        value: formData.touchLevel,
        unit: 'inches'
      });
    } else {
      if (formData.customHeight.meters) {
        measurements.push({
          type: 'custom',
          value: parseFloat(formData.customHeight.meters),
          unit: 'meters'
        });
      } else {
        measurements.push({
          type: 'custom',
          value: parseInt(formData.customHeight.feet),
          unit: 'feet'
        });
        if (formData.customHeight.inches) {
          measurements.push({
            type: 'custom',
            value: parseInt(formData.customHeight.inches),
            unit: 'inches'
          });
        }
      }
    }

    return onSave({
      measurements,
      notes: formData.notes,
      photos: formData.photos,
      railroadDetails: {
        crossingType: formData.crossingType,
        crossingNumber: formData.crossingNumber,
        surfaceType: formData.surfaceType,
        warningDevices: formData.warningDevices,
        trackCount: parseInt(formData.trackCount) || null,
        railroadCompany: formData.railroadCompany || null,
        emergencyPhone: formData.emergencyPhone || null
      }
    });
  };

  return (
    <BasePOIModal
      type={type}
      poiInfo={poiInfo}
      onClose={onClose}
      onSave={handleSubmit}
      onBack={onBack}
    >
      <div className="space-y-6">
        <MeasurementSelector
          measurementType={formData.measurementType}
          setMeasurementType={(type) => setFormData(prev => ({ ...prev, measurementType: type }))}
          touchLevel={formData.touchLevel}
          setTouchLevel={(level) => setFormData(prev => ({ ...prev, touchLevel: level }))}
          customHeight={formData.customHeight}
          setCustomHeight={(height) => setFormData(prev => ({ ...prev, customHeight: height }))}
        />

        <WidthClearance 
          width={formData.width}
          setWidth={(width) => setFormData(prev => ({ ...prev, width }))}
        />

        <div>
          <label className="block text-gray-400 mb-2">Crossing Type</label>
          <select
            value={formData.crossingType}
            onChange={(e) => setFormData(prev => ({ ...prev, crossingType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Crossing Type</option>
            {CROSSING_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Crossing Number</label>
          <input
            type="text"
            value={formData.crossingNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, crossingNumber: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter crossing number"
          />
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Surface Type</label>
          <select
            value={formData.surfaceType}
            onChange={(e) => setFormData(prev => ({ ...prev, surfaceType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Surface Type</option>
            {SURFACE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Number of Tracks</label>
          <input
            type="number"
            value={formData.trackCount}
            onChange={(e) => setFormData(prev => ({ ...prev, trackCount: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            min="1"
            placeholder="Enter number of tracks"
          />
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Warning Devices</label>
          <div className="space-y-2">
            {WARNING_DEVICES.map(device => (
              <label key={device.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.warningDevices.includes(device.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData(prev => ({
                        ...prev,
                        warningDevices: [...prev.warningDevices, device.value]
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        warningDevices: prev.warningDevices.filter(d => d !== device.value)
                      }));
                    }
                  }}
                  className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                />
                <span className="text-gray-300">{device.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Railroad Company</label>
          <input
            type="text"
            value={formData.railroadCompany}
            onChange={(e) => setFormData(prev => ({ ...prev, railroadCompany: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter railroad company name"
          />
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Emergency Phone</label>
          <input
            type="tel"
            value={formData.emergencyPhone}
            onChange={(e) => setFormData(prev => ({ ...prev, emergencyPhone: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter emergency phone number"
          />
        </div>

        <NotesAndPhotos
          notes={formData.notes}
          setNotes={(notes) => setFormData(prev => ({ ...prev, notes }))}
          photos={formData.photos}
          setPhotos={(photos) => setFormData(prev => ({ ...prev, photos }))}
        />
      </div>
    </BasePOIModal>
  );
};

export default RailroadPOIModal;